<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="사용자 통계를 불러오는 중..." />
    </div>

    <!-- 에러 상태 -->
    <div v-else-if="error" :class="$style.error">
      <p>{{ error }}</p>
      <button @click="fetchUserStats" :class="$style.retryButton">다시 시도</button>
    </div>

    <!-- 통계 데이터 -->
    <div v-else>
      <div :class="$style.header">
        <h1 :class="$style.title">👥 사용자 통계</h1>
        <p :class="$style.subtitle">가입한 사용자들의 활동 통계</p>
      </div>

      <div :class="$style.periodFilterSection">
        <div :class="$style.periodFilterTitle">월별 보기</div>
        <div :class="$style.periodFilterControls">
          <input v-model="selectedMonth" type="month" :class="$style.monthInput" />
          <button v-if="isMonthFilterActive" @click="resetSelectedMonth" :class="$style.periodResetButton">
            월 선택 해제
          </button>
        </div>
        <p v-if="isMonthFilterActive" :class="$style.periodFilterHint">
          선택 월: {{ selectedMonth }}
        </p>
      </div>

      <!-- 요약 정보 -->
      <div :class="$style.summary">
        <div :class="$style.summaryCard">
          <h3>총 사용자</h3>
          <p :class="$style.summaryNumber">{{ totalUsers }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>카카오 로그인</h3>
          <p :class="$style.summaryNumber">{{ kakaoUsers }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>X 연동 유저</h3>
          <p :class="$style.summaryNumber">{{ twitterUsers }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>{{ joinCountTitle }}</h3>
          <p :class="$style.summaryNumber">{{ monthlyNewUsers }}</p>
        </div>
        <div :class="$style.summaryCard">
          <h3>활성 사용자</h3>
          <p :class="$style.summaryNumber">{{ activeUsers }}</p>
        </div>
      </div>

      <!-- profileImage 필드 정리 -->
      <div v-if="usersWithProfileImage > 0" :class="$style.cleanupSection">
        <div :class="$style.cleanupInfo">
          <span :class="$style.warningIcon">⚠️</span>
          <span>레거시 profileImage 필드가 있는 사용자: <strong>{{ usersWithProfileImage }}명</strong></span>
        </div>
        <button :class="$style.cleanupButton" @click="cleanupProfileImageFields" :disabled="cleanupLoading">
          {{ cleanupLoading ? '정리 중...' : 'profileImage 필드 일괄 삭제' }}
        </button>
      </div>

      <!-- 사용자 목록 -->
      <div :class="$style.userList">
        <div :class="$style.sectionHeader">
          <h2>사용자 목록</h2>
          <div :class="$style.controls">
            <input v-model="searchQuery" type="text" :class="$style.searchInput" placeholder="닉네임 또는 이메일로 검색..." />
            <select v-model="sortBy" :class="$style.select">
              <option value="lastLogin">최근 로그인순</option>
              <option value="updatedAt">최근 업데이트순</option>
              <option value="joinDate">가입일순</option>
              <option value="nickname">닉네임순</option>
              <option value="provider">로그인 방식별</option>
            </select>
            <select v-model="filterProvider" :class="$style.select">
              <option value="all">모든 사용자</option>
              <option value="kakao">카카오 사용자</option>
              <option value="twitter">X 사용자</option>
            </select>
            <button :class="$style.alimtalkTestBtn" @click="openAlimtalkModal">
              알림톡 테스트
            </button>
          </div>
        </div>

        <!-- 사용자 카드들 -->
        <div :class="$style.userGrid">
          <div v-for="user in paginatedUsers" :key="user.email" :class="$style.userCard">
            <div :class="$style.userAvatar">
              <img v-if="user.profileImageUrl" :src="user.profileImageUrl" :alt="user.nickname"
                @error="handleImageError" />
              <img v-else :src="require('@/assets/images/default_profile.png')" :alt="user.nickname || '기본 프로필'" />
            </div>
            <div :class="$style.userInfo">
              <h3 :class="$style.userName">
                {{ user.nickname || "이름 없음" }}
              </h3>
              <p :class="$style.userEmail">{{ user.email }}</p>
              <div :class="$style.userMeta">
                <div :class="$style.providerList">
                  <!-- 새로운 authProviders 구조 표시 -->
                  <template v-if="user.authProviders && user.authProviders.length > 0">
                    <span v-for="authProvider in user.authProviders" :key="authProvider.provider"
                      :class="[$style.provider, $style[authProvider.provider]]">
                      {{ getProviderDisplayName(authProvider.provider) }}
                    </span>
                  </template>
                  <!-- 레거시 구조 표시 -->
                  <template v-else-if="user.provider">
                    <span :class="[$style.provider, $style[user.provider]]">
                      {{ getProviderDisplayName(user.provider) }}
                    </span>
                  </template>
                  <!-- provider 정보가 없는 경우 -->
                  <template v-else>
                    <span :class="[$style.provider, $style.unknown]"> 알 수 없음 </span>
                  </template>
                </div>
                <span :class="$style.joinDate">
                  {{ formatDate(user.joinDate) }}
                </span>
              </div>
              <div v-if="user.lastLogin" :class="$style.lastLogin">
                최근 로그인: {{ formatDate(user.lastLogin) }}
              </div>
              <div v-if="user.updatedAt" :class="$style.lastLogin">
                최근 업데이트: {{ formatDateTime(user.updatedAt) }}
              </div>
              <div :class="$style.lastLogin">
                가입일: {{ formatDate(user.joinDate) }}
              </div>
              <div v-if="user.detailLink" :class="$style.detailLink">X: {{ user.detailLink }}</div>
              <div :class="$style.authContainer">
                <label :class="$style.authLabel">권한:</label>
                <select :value="user.auth || ''" @change="updateUserAuth(user, $event.target.value)"
                  :class="$style.authSelect">
                  <option v-for="option in authOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div :class="$style.blockButtonContainer">
                <span v-if="user.isBlocked" :class="$style.blockedBadge">차단됨</span>
                <button @click="toggleBlock(user)"
                  :class="[$style.blockButton, { [$style.unblockButton]: user.isBlocked }]">
                  {{ user.isBlocked ? '차단 해제' : '차단' }}
                </button>
              </div>
              <div :class="$style.backupButtonContainer">
                <button v-if="!user.id.endsWith('_backup')" @click="backupUser(user)" :class="$style.backupButton"
                  :disabled="user.backupLoading">
                  {{ user.backupLoading ? '백업 중...' : '백업' }}
                </button>
                <button v-else @click="restoreUser(user)" :class="$style.restoreButton" :disabled="user.restoreLoading">
                  {{ user.restoreLoading ? '원복 중...' : '원복' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 페이지네이션 -->
        <div v-if="totalPages > 1" :class="$style.pagination">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" :class="$style.pageButton">
            이전
          </button>
          <button v-for="page in displayPages" :key="page" @click="goToPage(page)"
            :class="[$style.pageButton, { [$style.active]: page === currentPage }]">
            {{ page }}
          </button>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages" :class="$style.pageButton">
            다음
          </button>
        </div>
      </div>
    </div>

    <!-- 알림톡 테스트 모달 -->
    <div v-if="showAlimtalkModal" :class="$style.modalOverlay" @click.self="closeAlimtalkModal">
      <div :class="$style.alimtalkModal">

        <div :class="$style.modalHeader">
          <h2>알림톡 발송 테스트</h2>
          <button :class="$style.modalCloseBtn" @click="closeAlimtalkModal">✕</button>
        </div>

        <div :class="$style.modalBody">

          <!-- 수신번호 -->
          <div :class="$style.modalField">
            <label>수신 전화번호 <span :class="$style.required">*</span></label>
            <input v-model="alimtalkForm.phone" type="tel" placeholder="01012345678" :class="$style.modalInput" />
          </div>

          <!-- 치환 변수 -->
          <div :class="$style.modalSectionTitle">템플릿 치환 변수</div>
          <div :class="$style.twoCol">
            <div :class="$style.modalField">
              <label>이름</label>
              <input v-model="alimtalkForm.name" type="text" placeholder="홍길동" :class="$style.modalInput" />
            </div>
            <div :class="$style.modalField">
              <label>티켓 번호 #{3}</label>
              <input v-model="alimtalkForm.ticketNumber" type="text" placeholder="A-0001" :class="$style.modalInput" />
            </div>
          </div>
          <div :class="$style.modalField">
            <label>공연명 #{1}</label>
            <input v-model="alimtalkForm.liveName" type="text" placeholder="공연 이름" :class="$style.modalInput" />
          </div>
          <div :class="$style.modalField">
            <label>공연 일시 #{2}</label>
            <input v-model="alimtalkForm.liveDate" type="text" placeholder="2025년 1월 1일 19:00"
              :class="$style.modalInput" />
          </div>
          <div :class="$style.modalField">
            <label>유의사항 #{4}</label>
            <textarea v-model="alimtalkForm.notice" placeholder="입장 시 본인 확인 필요" :class="$style.modalTextarea"
              rows="2"></textarea>
          </div>

          <!-- 미리보기 -->
          <div :class="$style.previewBox">
            <div :class="$style.previewLabel">미리보기</div>
            <pre :class="$style.previewText">{{ alimtalkPreview() }}</pre>
          </div>

          <!-- 발송 결과 -->
          <div v-if="alimtalkResult"
            :class="[$style.messageResult, alimtalkResult.success ? $style.messageResultOk : $style.messageResultErr]">
            <p :class="$style.messageResultTitle">{{ alimtalkResult.text }}</p>
            <p v-if="alimtalkResult.trId" :class="$style.messageResultMeta">트래킹 ID: {{ alimtalkResult.trId }}</p>
            <ul v-if="alimtalkResult.details && alimtalkResult.details.length" :class="$style.messageResultDetail">
              <li v-for="(d, i) in alimtalkResult.details" :key="i"
                :class="d.code === 'A000' ? $style.detailOk : $style.detailFail">{{ d.to }} — {{ d.code === 'A000' ?
                  '성공' :
                  `실패 (${d.result || d.code})` }}</li>
            </ul>
          </div>

        </div>

        <div :class="$style.modalFooter">
          <button :class="$style.modalCancelBtn" @click="closeAlimtalkModal" :disabled="alimtalkSending">닫기</button>
          <button :class="$style.modalSendBtn" @click="sendAlimtalkTest"
            :disabled="alimtalkSending || !alimtalkForm.phone">{{ alimtalkSending ? '발송 중...' : '테스트 발송' }}</button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/utils/api";
import { sendAlimtalk } from "@/utils/write/message";
import StatsTabs from "@/components/StatsTabs.vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "/firebaseConfig";

export default {
  name: "UserStats",
  components: {
    StatsTabs,
    LoadingScreen,
  },
  data() {
    return {
      loading: false,
      error: null,
      users: [],
      currentPage: 1,
      usersPerPage: 20,
      sortBy: "lastLogin",
      filterProvider: "all",
      searchQuery: "",
      selectedMonth: "",
      authOptions: [
        { value: "", label: "권한 없음" },
        { value: "admin", label: "관리자" },
        { value: "beta", label: "베타 테스터" },
      ],
      cleanupLoading: false,

      // 알림톡 테스트 모달
      showAlimtalkModal: false,
      alimtalkSending: false,
      alimtalkResult: null,
      alimtalkForm: {
        phone: '',
        name: '',
        liveName: '',
        liveDate: '',
        ticketNumber: '',
        notice: '',
      },
    };
  },
  computed: {
    isMonthFilterActive() {
      return !!this.selectedMonth;
    },
    selectedMonthStartDate() {
      return this.selectedMonth ? this.parseMonthStart(this.selectedMonth) : null;
    },
    selectedMonthEndDate() {
      return this.selectedMonth ? this.parseMonthEnd(this.selectedMonth) : null;
    },
    statsBaseUsers() {
      if (!this.isMonthFilterActive) {
        return this.users;
      }

      return this.users.filter((user) => {
        const joinDate = this.parseDate(user.joinDate || user.createdAt);
        if (!joinDate) return false;

        if (this.selectedMonthStartDate && joinDate < this.selectedMonthStartDate) {
          return false;
        }
        if (this.selectedMonthEndDate && joinDate > this.selectedMonthEndDate) {
          return false;
        }
        return true;
      });
    },
    joinCountTitle() {
      return this.isMonthFilterActive ? "선택 월 가입" : "이번 달 가입";
    },
    // 요약 통계
    totalUsers() {
      return this.statsBaseUsers.length;
    },
    kakaoUsers() {
      return this.statsBaseUsers.filter((user) => {
        // 새로운 authProviders 구조 확인
        if (user.authProviders) {
          return user.authProviders.some((ap) => ap.provider === "kakao");
        }
        // 레거시 구조 확인
        return user.provider === "kakao";
      }).length;
    },
    twitterUsers() {
      return this.statsBaseUsers.filter((user) => {
        return user.detailLink;
      }).length;
    },
    monthlyNewUsers() {
      if (this.isMonthFilterActive) {
        return this.statsBaseUsers.length;
      }

      const thisMonth = new Date();
      thisMonth.setDate(1);
      return this.statsBaseUsers.filter((user) => {
        const joinDate = this.parseDate(user.joinDate);
        if (!joinDate) return false;
        return joinDate >= thisMonth;
      }).length;
    },
    activeUsers() {
      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      return this.statsBaseUsers.filter((user) => {
        if (!user.lastLogin) return false;
        const loginDate = this.parseDate(user.lastLogin);
        if (!loginDate) return false;
        return loginDate >= lastWeek;
      }).length;
    },
    usersWithProfileImage() {
      // profileImage 필드가 존재하는 경우 (값이 null이어도 포함)
      return this.users.filter((user) => 'profileImage' in user).length;
    },

    // 필터링된 사용자 목록
    filteredUsers() {
      let filtered = [...this.statsBaseUsers];

      // 검색어 필터링
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.trim().toLowerCase();
        filtered = filtered.filter((user) => {
          const nickname = (user.nickname || "").toLowerCase();
          const email = (user.email || "").toLowerCase();
          return nickname.includes(query) || email.includes(query);
        });
      }

      // 제공자별 필터링
      if (this.filterProvider !== "all") {
        filtered = filtered.filter((user) => {
          // 새로운 authProviders 구조 확인
          if (user.authProviders) {
            return user.authProviders.some((ap) => ap.provider === this.filterProvider);
          }
          // 레거시 구조 확인
          return user.provider === this.filterProvider;
        });
      }

      // 정렬
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case "lastLogin": {
            const aLogin = this.parseDate(a.lastLogin);
            const bLogin = this.parseDate(b.lastLogin);
            // 둘 다 null이면 0 (순서 유지)
            if (!aLogin && !bLogin) return 0;
            // a만 null이면 뒤로 (b가 먼저)
            if (!aLogin) return 1;
            // b만 null이면 앞으로 (a가 먼저)
            if (!bLogin) return -1;
            // 둘 다 있으면 최근 날짜순 (내림차순)
            return bLogin - aLogin;
          }
          case "updatedAt": {
            const aUpdate = this.parseDate(a.updatedAt);
            const bUpdate = this.parseDate(b.updatedAt);
            // 둘 다 null이면 0 (순서 유지)
            if (!aUpdate && !bUpdate) return 0;
            // a만 null이면 뒤로 (b가 먼저)
            if (!aUpdate) return 1;
            // b만 null이면 앞으로 (a가 먼저)
            if (!bUpdate) return -1;
            // 둘 다 있으면 최근 날짜순 (내림차순)
            return bUpdate - aUpdate;
          }
          case "joinDate": {
            const aJoin = this.parseDate(a.joinDate || a.createdAt);
            const bJoin = this.parseDate(b.joinDate || b.createdAt);
            // 둘 다 null이면 0 (순서 유지)
            if (!aJoin && !bJoin) return 0;
            // a만 null이면 뒤로 (b가 먼저)
            if (!aJoin) return 1;
            // b만 null이면 앞으로 (a가 먼저)
            if (!bJoin) return -1;
            // 둘 다 있으면 최근 날짜순 (내림차순)
            return bJoin - aJoin;
          }
          case "nickname":
            return (a.nickname || "").localeCompare(b.nickname || "");
          case "provider": {
            // 새로운 authProviders 구조 처리
            const getProviderForSort = (user) => {
              if (user.authProviders && user.authProviders.length > 0) {
                return user.authProviders[0].provider; // 첫 번째 provider로 정렬
              }
              return user.provider || "unknown";
            };
            return getProviderForSort(a).localeCompare(getProviderForSort(b));
          }
          default:
            return 0;
        }
      });

      return filtered;
    },

    // 페이지네이션
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.usersPerPage);
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.usersPerPage;
      const end = start + this.usersPerPage;
      return this.filteredUsers.slice(start, end);
    },
    displayPages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
  },
  watch: {
    sortBy() {
      this.currentPage = 1;
    },
    filterProvider() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    selectedMonth() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.fetchUserStats();
  },
  methods: {
    parseMonthStart(monthValue) {
      if (!monthValue) return null;
      const [year, month] = monthValue.split("-").map(Number);
      if (!year || !month) return null;
      return new Date(year, month - 1, 1, 0, 0, 0, 0);
    },
    parseMonthEnd(monthValue) {
      if (!monthValue) return null;
      const [year, month] = monthValue.split("-").map(Number);
      if (!year || !month) return null;
      return new Date(year, month, 0, 23, 59, 59, 999);
    },
    resetSelectedMonth() {
      this.selectedMonth = "";
    },
    // 안전한 날짜 파싱 헬퍼 함수
    parseDate(date) {
      if (!date) return null;

      try {
        // Firestore Timestamp인 경우 (toDate 메서드가 있는 경우)
        if (date.toDate && typeof date.toDate === "function") {
          return date.toDate();
        }

        // Object 타입인 경우 (Firestore Timestamp 객체일 수 있음)
        if (typeof date === "object" && date !== null && !(date instanceof Date)) {
          // Firestore Timestamp 형식: {seconds: number, nanoseconds: number}
          if (typeof date.seconds === "number") {
            // seconds를 밀리초로 변환 (nanoseconds는 밀리초 단위로 변환)
            const milliseconds = date.seconds * 1000 + (date.nanoseconds || 0) / 1000000;
            const d = new Date(milliseconds);
            if (!isNaN(d.getTime())) {
              return d;
            }
          }

          // 다른 객체 형식인 경우 문자열로 변환 시도
          if (date.toString && typeof date.toString === "function") {
            const dateString = date.toString();
            if (dateString !== "[object Object]") {
              const d = new Date(dateString);
              if (!isNaN(d.getTime())) {
                return d;
              }
            }
          }

          // 객체의 속성들을 확인하여 날짜 관련 정보 찾기
          if (date._seconds || date.seconds) {
            const seconds = date._seconds || date.seconds;
            const nanoseconds = date._nanoseconds || date.nanoseconds || 0;
            const milliseconds = seconds * 1000 + nanoseconds / 1000000;
            const d = new Date(milliseconds);
            if (!isNaN(d.getTime())) {
              return d;
            }
          }
        }

        // 문자열인 경우
        if (typeof date === "string") {
          // 공백 제거
          const trimmedDate = date.trim();

          // ISO 8601 형식 (2025-12-05T02:37:16.236Z) 직접 파싱 시도
          // new Date()는 ISO 8601 형식을 잘 파싱하므로 바로 시도
          let d = new Date(trimmedDate);
          if (!isNaN(d.getTime())) {
            return d;
          }

          // T가 포함된 경우 추가 처리 (타임존 정보가 없는 경우)
          if (trimmedDate.includes("T")) {
            // Z가 없으면 UTC로 간주하고 추가
            if (!trimmedDate.endsWith("Z") && !trimmedDate.match(/[+-]\d{2}:\d{2}$/)) {
              const withZ = trimmedDate + "Z";
              d = new Date(withZ);
              if (!isNaN(d.getTime())) {
                return d;
              }
            }
          }

          // 유효하지 않은 날짜인 경우 점(.)을 대시(-)로 변경하여 파싱 시도
          const normalizedDate = trimmedDate.replace(/\./g, "-");
          d = new Date(normalizedDate);
          if (!isNaN(d.getTime())) {
            return d;
          }

          // 디버깅: 파싱 실패한 경우 로그 출력
          console.warn("날짜 파싱 실패:", trimmedDate, "타입:", typeof date, "원본:", date);
          return null;
        }
        // Date 객체인 경우
        if (date instanceof Date) {
          return isNaN(date.getTime()) ? null : date;
        }
        // 숫자 타임스탬프인 경우
        if (typeof date === "number") {
          const d = new Date(date);
          return isNaN(d.getTime()) ? null : d;
        }
        // 그 외의 경우
        const d = new Date(date);
        return isNaN(d.getTime()) ? null : d;
      } catch (error) {
        console.error("날짜 파싱 오류:", error, date, "타입:", typeof date);
        return null;
      }
    },

    async fetchUserStats() {
      this.loading = true;
      this.error = null;

      try {
        const { getAllUsers } = await import("@/utils/read/user");
        const users = await getAllUsers("lastLogin", "desc");

        // 각 사용자의 프로필 이미지 URL 로드
        this.users = await Promise.all(
          users.map(async (user) => {
            if (user.img) {
              try {
                const imageRef = storageRef(storage, `profile/${user.img}`);
                user.profileImageUrl = await getDownloadURL(imageRef);
              } catch (error) {
                console.warn(`프로필 이미지 로드 실패 (${user.email}):`, error);
                user.profileImageUrl = null;
              }
            } else {
              user.profileImageUrl = null;
            }
            return user;
          })
        );
      } catch (error) {
        console.error("사용자 통계 로딩 오류:", error);
        this.error = "사용자 통계를 불러오는데 실패했습니다.";
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    formatDate(date) {
      if (!date) return "-";

      const d = this.parseDate(date);
      if (!d) {
        console.warn("날짜 파싱 실패 - formatDate:", date, "타입:", typeof date);
        return "-";
      }

      try {
        const formatted = d.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        // Invalid Date 체크
        if (formatted === "Invalid Date") {
          console.warn("Invalid Date 발생:", date, "파싱된 값:", d);
          return "-";
        }
        return formatted;
      } catch (error) {
        console.error("날짜 포맷 오류:", error, date);
        return "-";
      }
    },

    formatDateTime(date) {
      if (!date) return "-";

      const d = this.parseDate(date);
      if (!d) {
        return "-";
      }

      try {
        const formatted = d.toLocaleString("ko-KR", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
        if (formatted === "Invalid Date") {
          return "-";
        }
        return formatted;
      } catch (error) {
        console.error("날짜 포맷 오류:", error, date);
        return "-";
      }
    },

    handleImageError(event) {
      event.target.style.display = "none";
      event.target.parentNode.classList.add("error");
    },

    async cleanupProfileImageFields() {
      if (!confirm(`${this.usersWithProfileImage}명의 사용자에게서 profileImage 필드를 삭제합니다. 계속하시겠습니까?`)) {
        return;
      }

      this.cleanupLoading = true;
      try {
        const { deleteProfileImageField } = await import("@/utils/write/user");

        // profileImage 필드가 있는 사용자 이메일 목록 (값이 null이어도 포함)
        const usersToClean = this.users
          .filter((user) => 'profileImage' in user)
          .map((user) => user.email);

        let successCount = 0;
        let failCount = 0;

        for (const email of usersToClean) {
          try {
            await deleteProfileImageField(email);
            successCount++;
          } catch (error) {
            console.error(`${email} profileImage 삭제 실패:`, error);
            failCount++;
          }
        }

        // 결과 알림
        if (failCount === 0) {
          alert(`✅ ${successCount}명의 사용자에게서 profileImage 필드를 성공적으로 삭제했습니다.`);
        } else {
          alert(`⚠️ ${successCount}명 성공, ${failCount}명 실패`);
        }

        // 사용자 목록 새로고침
        await this.fetchUserStats();
      } catch (error) {
        console.error("profileImage 필드 정리 오류:", error);
        alert("profileImage 필드 정리에 실패했습니다.");
      } finally {
        this.cleanupLoading = false;
      }
    },

    getProviderDisplayName(provider) {
      const providerNames = {
        kakao: "카카오",
        twitter: "X",
        password: "이메일",
        google: "구글",
        apple: "애플",
      };
      return providerNames[provider] || provider;
    },

    async toggleBlock(user) {
      try {
        const response = await apiClient.patch(`/api/user/${encodeURIComponent(user.email)}/block`);

        if (response.data.success) {
          // 로컬 상태 업데이트
          user.isBlocked = response.data.data.isBlocked;
          alert(response.data.message);
        } else {
          alert(response.data.message || "차단 상태 변경에 실패했습니다.");
        }
      } catch (error) {
        console.error("차단 상태 변경 오류:", error);
        alert(error.response?.data?.message || "차단 상태 변경에 실패했습니다.");
      }
    },

    async updateUserAuth(user, newAuth) {
      const authLabel = this.authOptions.find(opt => opt.value === newAuth)?.label || "권한 없음";
      const confirmed = confirm(`${user.nickname || user.email}님의 권한을 "${authLabel}"(으)로 변경하시겠습니까?`);

      if (!confirmed) {
        return;
      }

      try {
        await apiClient.patch(`/api/user/${encodeURIComponent(user.email)}/auth`, {
          auth: newAuth || "",
        });

        // 로컬 상태 업데이트
        user.auth = newAuth;
        alert(`${user.nickname || user.email}님의 권한이 "${authLabel}"(으)로 변경되었습니다.`);
      } catch (error) {
        console.error("권한 변경 오류:", error);
        alert("권한 변경에 실패했습니다.");
      }
    },

    async backupUser(user) {
      const confirmed = confirm(`${user.nickname || user.email}님의 데이터를 백업하시겠습니까?\n백업된 데이터는 "${user.id}_backup"으로 저장됩니다.`);

      if (!confirmed) {
        return;
      }

      user.backupLoading = true;

      try {
        const response = await apiClient.post(`/api/user/${encodeURIComponent(user.id)}/backup`);

        if (response.data.success) {
          alert(`✅ ${response.data.message}\n백업 문서: ${response.data.data.backupEmail}\n복사된 서브컬렉션: ${response.data.data.copiedSubcollections}개`);

          // 사용자 목록 새로고침
          await this.fetchUserStats();
        } else {
          alert(response.data.message || "백업에 실패했습니다.");
        }
      } catch (error) {
        console.error("백업 오류:", error);
        alert(error.response?.data?.message || "백업에 실패했습니다.");
      } finally {
        user.backupLoading = false;
      }
    },

    async restoreUser(user) {
      const originalId = user.id.replace(/_backup$/, '');
      const confirmed = confirm(`백업 데이터를 원복하시겠습니까?\n"${user.id}"의 데이터가 "${originalId}"로 복원되고, 백업 문서는 삭제됩니다.`);

      if (!confirmed) {
        return;
      }

      user.restoreLoading = true;

      try {
        const response = await apiClient.post(`/api/user/${encodeURIComponent(user.id)}/restore`);

        if (response.data.success) {
          alert(`✅ ${response.data.message}\n원본 문서: ${response.data.data.originalEmail}\n복원된 서브컬렉션: ${response.data.data.restoredSubcollections}개`);

          // 사용자 목록 새로고침
          await this.fetchUserStats();
        } else {
          alert(response.data.message || "원복에 실패했습니다.");
        }
      } catch (error) {
        console.error("원복 오류:", error);
        alert(error.response?.data?.message || "원복에 실패했습니다.");
      } finally {
        user.restoreLoading = false;
      }
    },

    // ── 알림톡 테스트 ──────────────────────────────────────────
    openAlimtalkModal() {
      this.alimtalkResult = null;
      this.showAlimtalkModal = true;
    },
    closeAlimtalkModal() {
      if (this.alimtalkSending) return;
      this.showAlimtalkModal = false;
      this.alimtalkResult = null;
    },
    alimtalkPreview() {
      const f = this.alimtalkForm;
      return `안녕하세요 ${f.name || '#{이름}'} 고객님,\n구매하신 공연 티켓이 발급 완료되어 안내해 드립니다.\n\n▶ 공연명 : ${f.liveName || '#{1}'}\n▶ 공연 일시 : ${f.liveDate || '#{2}'}\n▶ 유의 사항 : ${f.notice || '#{4}'}\n▶ 티켓 번호 : ${f.ticketNumber || '#{3}'}\n\n공연 일시 및 유의 사항을 확인하시고 즐거운 관람되시길 바랍니다.\n감사합니다.`;
    },
    async sendAlimtalkTest() {
      if (this.alimtalkSending) return;
      const f = this.alimtalkForm;
      if (!f.phone) { alert('수신 전화번호를 입력해주세요.'); return; }

      this.alimtalkSending = true;
      this.alimtalkResult = null;

      try {
        const result = await sendAlimtalk({
          type: 'ticket_issued',
          destinations: [
            {
              to: f.phone,
              replaceWords: {
                '이름': f.name,
                '1': f.liveName,
                '2': f.liveDate,
                '3': f.ticketNumber,
                '4': f.notice,
              },
            },
          ],
          ref: 'alimtalk-test',
        });

        const { successCount, failCount, total, trId, destinations } = result;
        this.alimtalkResult = {
          success: failCount === 0,
          text: failCount === 0
            ? `✅ 발송 성공 (총 ${total}명)`
            : `✅ ${successCount}명 성공 / ❌ ${failCount}명 실패`,
          trId,
          details: destinations,
        };
      } catch (err) {
        console.error('알림톡 테스트 오류:', err);
        const errMsg = err.response?.data?.message || err.message || '알 수 없는 오류';
        this.alimtalkResult = { success: false, text: `❌ 발송 실패: ${errMsg}`, trId: '', details: [] };
      } finally {
        this.alimtalkSending = false;
      }
    },
  },
};
</script>

<style module>
.container {
  width: 100%;
  max-width: 1024px;
  padding: 0 15px;
  margin: 0 auto;
}

.error {
  text-align: center;
  padding: 40px;
  color: #d32f2f;
}

.retryButton {
  background: #2196f3;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 10px;
}

.retryButton:hover {
  background: #1976d2;
}

.header {
  text-align: center;
  margin-bottom: 25px;
  padding: 15px 0;
}

.title {
  font-size: 2.2rem;
  font-weight: bold;
  color: #333;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 1rem;
  color: #666;
  margin: 0;
}

.periodFilterSection {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafcff;
}

.periodFilterTitle {
  font-size: 0.9rem;
  color: #444;
  font-weight: 700;
}

.periodFilterControls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.monthInput {
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 13px;
}

.monthInput:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.periodSeparator {
  color: #777;
  font-size: 0.85rem;
}

.periodResetButton {
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #555;
  font-size: 13px;
  cursor: pointer;
}

.periodResetButton:hover {
  border-color: #bbb;
  background: #f5f5f5;
}

.periodFilterHint {
  margin: 0;
  font-size: 0.8rem;
  color: #666;
}

.summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 30px;
}

.summaryCard {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e0e0e0;
}

.summaryCard h3 {
  margin: 0 0 12px 0;
  font-size: 0.9rem;
  color: #666;
  font-weight: 600;
}

.summaryNumber {
  font-size: 1.8rem;
  font-weight: bold;
  color: #2196f3;
  margin: 0;
}

.cleanupSection {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 20px;
}

.cleanupInfo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  color: #856404;
}

.warningIcon {
  font-size: 1.2rem;
}

.cleanupButton {
  background: #dc3545;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.cleanupButton:hover:not(:disabled) {
  background: #c82333;
}

.cleanupButton:disabled {
  background: #aaa;
  cursor: not-allowed;
}

.userList {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.sectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
}

.sectionHeader h2 {
  margin: 0;
  color: #333;
  font-size: 1.2rem;
}

.controls {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.searchInput {
  padding: 7px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 13px;
  min-width: 200px;
}

.searchInput:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.searchInput::placeholder {
  color: #999;
}

.select {
  padding: 7px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 13px;
  min-width: 110px;
}

.userGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
  padding: 20px;
}

.userCard {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: all 0.2s;
}

.userCard:hover {
  border-color: #2196f3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.1);
}

.userAvatar {
  flex-shrink: 0;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.userAvatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.defaultAvatar {
  width: 100%;
  height: 100%;
  background: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
  font-weight: bold;
}

.userInfo {
  flex: 1;
  min-width: 0;
}

.userName {
  margin: 0 0 4px 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.userEmail {
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.userMeta {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.provider {
  padding: 2px 7px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.provider.kakao {
  background: #fee500;
  color: #3c1e1e;
}

.provider.twitter {
  background: #1da1f2;
  color: white;
}

.provider.password {
  background: #607d8b;
  color: white;
}

.provider.google {
  background: #4285f4;
  color: white;
}

.provider.apple {
  background: #000000;
  color: white;
}

.provider.unknown {
  background: #9e9e9e;
  color: white;
}

.providerList {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 5px;
}

.joinDate,
.lastLogin,
.detailLink {
  padding-top: 3px;
  font-size: 0.75rem;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
}

.detailLink {
  color: #66bdff;
  text-decoration: none;
  font-size: 13px;
  word-break: break-all;
}

.authContainer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
}

.authLabel {
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
  white-space: nowrap;
}

.authSelect {
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  font-size: 0.8rem;
  cursor: pointer;
  flex: 1;
  min-width: 100px;
}

.authSelect:focus {
  outline: none;
  border-color: #2196f3;
}

.blockButtonContainer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.blockedBadge {
  background: #ffebee;
  color: #d32f2f;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.blockButton {
  padding: 4px 10px;
  border: 1px solid #d32f2f;
  background: white;
  color: #d32f2f;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.blockButton:hover {
  background: #d32f2f;
  color: white;
}

.unblockButton {
  border-color: #4caf50;
  color: #4caf50;
}

.backupButtonContainer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.backupButton {
  padding: 4px 10px;
  border: 1px solid #2196f3;
  background: white;
  color: #2196f3;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.backupButton:hover:not(:disabled) {
  background: #2196f3;
  color: white;
}

.backupButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.restoreButton {
  padding: 4px 10px;
  border: 1px solid #ff9800;
  background: white;
  color: #ff9800;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.restoreButton:hover:not(:disabled) {
  background: #ff9800;
  color: white;
}

.restoreButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}


.unblockButton:hover {
  background: #4caf50;
  color: white;
}

/* 페이지네이션 스타일 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 25px;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 60px;
}

.pageButton {
  padding: 7px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #ffffff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 32px;
  text-align: center;
}

.pageButton:hover:not(.active):not(:disabled) {
  background: #f8f9fa;
  border-color: #ccc;
  color: #333;
}

.pageButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: #ccc;
  background: #f8f9fa;
}

.pageButton.active {
  background: #222;
  color: #ffffff;
  border-color: #222;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px;
  }

  .error {
    padding: 30px 15px;
  }

  .retryButton {
    padding: 8px 16px;
    font-size: 13px;
    border-radius: 6px;
  }

  .header {
    margin-bottom: 20px;
    padding: 10px 0;
  }

  .title {
    font-size: 1.7rem;
    margin-bottom: 6px;
  }

  .subtitle {
    font-size: 0.9rem;
  }

  .periodFilterSection {
    padding: 12px;
    margin-bottom: 14px;
  }

  .periodFilterControls {
    width: 100%;
    gap: 6px;
  }

  .monthInput {
    flex: 1;
    min-width: 0;
    padding: 8px 10px;
  }

  .periodResetButton {
    width: 100%;
    padding: 9px 12px;
  }

  .summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .summaryCard {
    padding: 15px 10px;
    border-radius: 8px;
  }

  .summaryCard h3 {
    font-size: 0.75rem;
    margin-bottom: 8px;
  }

  .summaryNumber {
    font-size: 1.4rem;
  }

  .cleanupSection {
    flex-direction: column;
    gap: 12px;
    text-align: center;
    padding: 12px 15px;
  }

  .cleanupInfo {
    flex-direction: column;
    gap: 5px;
  }

  .cleanupButton {
    width: 100%;
    padding: 12px;
  }

  .userList {
    border-radius: 8px;
  }

  .sectionHeader {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
    padding: 15px;
  }

  .sectionHeader h2 {
    font-size: 1.1rem;
    text-align: center;
  }

  .controls {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .select {
    width: 100%;
    padding: 10px 12px;
    font-size: 13px;
    min-width: auto;
    border-radius: 6px;
  }

  .searchInput {
    width: 100%;
    padding: 10px 12px;
    font-size: 13px;
    min-width: auto;
    border-radius: 6px;
  }

  .userGrid {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 15px;
  }

  .userCard {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 15px;
  }

  .userAvatar {
    width: 60px;
    height: 60px;
    margin: 0 auto;
  }

  .defaultAvatar {
    font-size: 24px;
  }

  .userInfo {
    width: 100%;
  }

  .userName {
    font-size: 1rem;
    margin-bottom: 4px;
  }

  .userEmail {
    font-size: 0.85rem;
    margin-bottom: 8px;
  }

  .userMeta {
    flex-direction: column;
    gap: 6px;
    align-items: center;
  }

  .providerList {
    justify-content: center;
  }

  .provider {
    padding: 3px 8px;
    font-size: 0.75rem;
  }

  .joinDate,
  .lastLogin,
  .detailLink {
    font-size: 0.75rem;
  }

  .detailLink {
    font-size: 12px;
  }

  .authContainer {
    justify-content: center;
    padding: 8px 10px;
  }

  .authLabel {
    font-size: 0.75rem;
  }

  .authSelect {
    font-size: 0.75rem;
    padding: 6px 10px;
  }

  .blockButtonContainer {
    justify-content: center;
    gap: 8px;
    margin-top: 10px;
  }

  .blockedBadge {
    padding: 2px 6px;
    font-size: 0.7rem;
  }

  .blockButton {
    padding: 6px 14px;
    font-size: 0.8rem;
    border-radius: 5px;
  }

  .backupButtonContainer {
    justify-content: center;
    gap: 8px;
    margin-top: 10px;
  }

  .backupButton {
    padding: 6px 14px;
    font-size: 0.8rem;
    border-radius: 5px;
  }

  .restoreButton {
    padding: 6px 14px;
    font-size: 0.8rem;
    border-radius: 5px;
  }

  .pagination {
    margin-top: 20px;
    gap: 5px;
    margin-bottom: 50px;
  }

  .pageButton {
    padding: 6px 9px;
    font-size: 12px;
    min-width: 28px;
    border-radius: 5px;
  }
}

/* ── 알림톡 테스트 버튼 ── */
.alimtalkTestBtn {
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  background: #ff9800;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.alimtalkTestBtn:hover {
  background: #fb8c00;
}

/* ── 공통 모달 ── */
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.alimtalkModal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modalHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.modalHeader h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #222;
}

.modalCloseBtn {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #888;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  line-height: 1;
}

.modalCloseBtn:hover {
  background: #f0f0f0;
  color: #333;
}

.modalBody {
  overflow-y: auto;
  flex: 1;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.modalSectionTitle {
  font-size: 0.8rem;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}

.modalField {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modalField label {
  font-size: 0.8rem;
  color: #555;
  font-weight: 600;
}

.required {
  color: #e53935;
}

.twoCol {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.modalInput {
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 7px;
  font-size: 0.9rem;
  color: #333;
  background: #fafafa;
}

.modalInput:focus {
  outline: none;
  border-color: #ff9800;
  background: #fff;
}

.modalTextarea {
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 7px;
  font-size: 0.9rem;
  color: #333;
  background: #fafafa;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
}

.modalTextarea:focus {
  outline: none;
  border-color: #ff9800;
  background: #fff;
}

/* 미리보기 */
.previewBox {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 12px 14px;
}

.previewLabel {
  font-size: 0.75rem;
  font-weight: 700;
  color: #f57c00;
  margin-bottom: 6px;
}

.previewText {
  margin: 0;
  font-size: 0.85rem;
  color: #333;
  white-space: pre-wrap;
  line-height: 1.6;
  font-family: inherit;
}

/* 발송 결과 */
.messageResult {
  padding: 12px 16px;
  border-radius: 8px;
}

.messageResultOk {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.messageResultErr {
  background: #fce4e4;
  color: #c62828;
  border: 1px solid #ef9a9a;
}

.messageResultTitle {
  margin: 0 0 4px 0;
  font-weight: 700;
  font-size: 0.9rem;
}

.messageResultMeta {
  margin: 0 0 6px 0;
  font-size: 0.78rem;
  opacity: 0.75;
}

.messageResultDetail {
  margin: 6px 0 0 0;
  padding-left: 16px;
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detailOk {
  color: #2e7d32;
}

.detailFail {
  color: #c62828;
  font-weight: 600;
}

/* 모달 하단 */
.modalFooter {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.modalCancelBtn {
  padding: 10px 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #555;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.modalCancelBtn:hover:not(:disabled) {
  background: #f5f5f5;
}

.modalCancelBtn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modalSendBtn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #ff9800;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.modalSendBtn:hover:not(:disabled) {
  background: #fb8c00;
}

.modalSendBtn:disabled {
  background: #b0bec5;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .alimtalkModal {
    max-height: 95vh;
    border-radius: 10px;
  }

  .modalHeader,
  .modalBody,
  .modalFooter {
    padding-left: 16px;
    padding-right: 16px;
  }

  .twoCol {
    grid-template-columns: 1fr;
  }

  .modalSendBtn,
  .modalCancelBtn {
    flex: 1;
    text-align: center;
  }
}
</style>
